import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export const FINANCIAL_GROUPS = [
  {
    title: 'Income statement',
    subtitle: 'Performance during the selected period',
    labels: ['Sales revenue', 'Cost of sales', 'Gross profit', 'Operating profit', 'Profit before taxes', 'Net income'],
  },
  {
    title: 'Assets',
    subtitle: 'Liquidity and asset base at period end',
    labels: ['Cash and liquid assets', 'Accounts receivable', 'Inventory', 'Total current assets', 'Fixed assets', 'Total long-term assets', 'Total assets'],
  },
  {
    title: 'Liabilities & equity',
    subtitle: 'Obligations, capital and working capital',
    labels: ['Accounts payable', 'Total current liabilities', 'Long-term debt', 'Total long-term liabilities', 'Total liabilities', 'Tangible net worth', 'Net worth', 'Working capital'],
  },
  {
    title: 'Liquidity ratios',
    subtitle: 'Short-term financial capacity',
    labels: ['Current ratio', 'Quick ratio'],
  },
];

const cleanMarkdownValue = (value = '') => String(value)
  .replace(/\\\|/g, '|')
  .replace(/\*\*/g, '')
  .trim();

const markdownField = (markdown, label) => {
  const match = String(markdown || '').match(new RegExp(`\\*\\*${label}:\\*\\*\\s*([^\\n]+)`, 'i'));
  return cleanMarkdownValue(match?.[1] || '');
};

export function parseDnbFinancialReport(markdown) {
  const lines = String(markdown || '').split(/\r?\n/);
  const statements = [];
  let statement = null;
  let table = null;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    const heading = line.match(/^##\s+Statement ending\s+(.+)$/i);
    if (heading) {
      statement = { periodEnd: cleanMarkdownValue(heading[1]), details: {}, metrics: [] };
      statements.push(statement);
      table = null;
      return;
    }
    if (!statement) return;
    if (/^\|\s*Statement detail\s*\|/i.test(line)) {
      table = 'details';
      return;
    }
    if (/^\|\s*Financial item\s*\|/i.test(line)) {
      table = 'metrics';
      return;
    }
    if (!line.startsWith('|') || /^\|\s*-+/.test(line)) return;

    const cells = line.slice(1, -1).split(/(?<!\\)\|/).map(cleanMarkdownValue);
    if (cells.length < 2 || !cells[0]) return;
    if (table === 'details') statement.details[cells[0]] = cells[1];
    if (table === 'metrics') statement.metrics.push({ label: cells[0], value: cells[1] });
  });

  statements.forEach((item) => {
    item.periodStart = item.details['Period start'] || '—';
    item.periodEnd = item.details['Period end'] || item.periodEnd;
    item.duration = item.details.Duration || '—';
    item.scope = item.details.Scope || '—';
    item.reliability = item.details.Reliability || '—';
    item.currency = item.details.Currency || '—';
    item.metricsByLabel = Object.fromEntries(item.metrics.map((metric) => [metric.label, metric.value]));
  });

  return {
    companyName: markdownField(markdown, 'Company') || 'Company',
    source: markdownField(markdown, 'Source') || 'Dun & Bradstreet (D&B)',
    dunsNumber: markdownField(markdown, 'D-U-N-S'),
    retrievedAt: markdownField(markdown, 'Retrieved'),
    statements,
  };
}

export function displayFinancialValue(value, label = '', { compact = false } = {}) {
  if (!value || value === '—') return '—';
  const ratio = /ratio/i.test(label);
  const match = String(value).trim().match(/^([A-Z]{3})\s+(-?[\d,.]+)$/);
  if (!match) return value;
  const currency = ratio ? '' : match[1];
  const amount = Number(match[2].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return value;
  if (ratio) return amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (!compact) return `${currency} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

  const units = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  const unit = units.find(([threshold]) => Math.abs(amount) >= threshold);
  if (!unit) return `${currency} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return `${currency} ${(amount / unit[0]).toLocaleString('en-US', { maximumFractionDigits: 2 })}${unit[1]}`;
}

const safePdfText = (value = '') => String(value)
  .replace(/[–—−]/g, '-')
  .replace(/[‘’]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/[^\x20-\x7E]/g, '');

const brand = {
  navy: rgb(0.075, 0.145, 0.29),
  blue: rgb(0.145, 0.31, 0.61),
  green: rgb(0.37, 0.69, 0.29),
  ink: rgb(0.12, 0.17, 0.27),
  muted: rgb(0.4, 0.46, 0.56),
  border: rgb(0.88, 0.9, 0.94),
  surface: rgb(0.96, 0.97, 0.985),
  white: rgb(1, 1, 1),
};

export async function createDnbFinancialReportPdf({ report, logoBytes = null }) {
  const pdf = await PDFDocument.create();
  pdf.setTitle(`${report.companyName} Financial Report`);
  pdf.setAuthor('CrediArc');
  pdf.setCreator('CrediArc Risk Platform');
  pdf.setSubject('D&B financial statements');
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  let logo = null;
  if (logoBytes) {
    try { logo = await pdf.embedPng(logoBytes); } catch (error) { logo = null; }
  }

  const pageSize = [595.28, 841.89];
  const margin = 40;
  const contentWidth = pageSize[0] - margin * 2;

  const drawBrand = (page, compact = false) => {
    const height = page.getHeight();
    if (logo) {
      const width = compact ? 92 : 124;
      page.drawImage(logo, { x: margin, y: height - (compact ? 47 : 65), width, height: width / 2.656 });
    } else {
      page.drawText('CrediArc', { x: margin, y: height - (compact ? 35 : 50), size: compact ? 15 : 21, font: bold, color: brand.navy });
    }
    page.drawText('RISK INTELLIGENCE', { x: pageSize[0] - margin - 100, y: height - 35, size: 7.5, font: bold, color: brand.muted });
    page.drawLine({ start: { x: margin, y: height - (compact ? 58 : 78) }, end: { x: pageSize[0] - margin, y: height - (compact ? 58 : 78) }, thickness: 1, color: brand.border });
    return height - (compact ? 82 : 105);
  };

  const drawKpi = (page, x, y, width, label, value) => {
    page.drawRectangle({ x, y, width, height: 60, color: brand.surface, borderColor: brand.border, borderWidth: 0.7 });
    page.drawText(safePdfText(label).toUpperCase(), { x: x + 12, y: y + 40, size: 7.3, font: bold, color: brand.muted });
    page.drawText(safePdfText(value), { x: x + 12, y: y + 17, size: 14, font: bold, color: brand.navy });
  };

  const cover = pdf.addPage(pageSize);
  let y = drawBrand(cover);
  cover.drawText('Financial Report', { x: margin, y, size: 25, font: bold, color: brand.navy });
  y -= 27;
  cover.drawText(safePdfText(report.companyName), { x: margin, y, size: 14, font: bold, color: brand.ink });
  y -= 20;
  cover.drawText(`Source: D&B companyfinancials_L2_v2  |  D-U-N-S: ${safePdfText(report.dunsNumber || '-')}`, { x: margin, y, size: 8.5, font: regular, color: brand.muted });
  y -= 14;
  cover.drawText(`Retrieved: ${safePdfText(report.retrievedAt || '-')}`, { x: margin, y, size: 8.5, font: regular, color: brand.muted });
  y -= 30;

  const latest = report.statements[0];
  cover.drawText(`Latest statement  |  ${safePdfText(latest?.periodEnd || '-')}`, { x: margin, y, size: 11, font: bold, color: brand.ink });
  y -= 74;
  const latestKpis = ['Sales revenue', 'Gross profit', 'Net income', 'Total assets', 'Total liabilities', 'Net worth'];
  latestKpis.forEach((label, index) => {
    const column = index % 3;
    const row = Math.floor(index / 3);
    const width = (contentWidth - 20) / 3;
    drawKpi(cover, margin + column * (width + 10), y - row * 70, width, label, displayFinancialValue(latest?.metricsByLabel?.[label], label, { compact: true }));
  });
  y -= 174;
  cover.drawText('Statement history', { x: margin, y, size: 12, font: bold, color: brand.ink });
  y -= 24;
  const columns = [
    { label: 'PERIOD END', width: 88 },
    { label: 'DURATION', width: 62 },
    { label: 'REVENUE', width: 92 },
    { label: 'NET INCOME', width: 92 },
    { label: 'TOTAL ASSETS', width: 92 },
    { label: 'NET WORTH', width: 89 },
  ];
  let x = margin;
  cover.drawRectangle({ x: margin, y: y - 4, width: contentWidth, height: 22, color: brand.navy });
  columns.forEach((column) => {
    cover.drawText(column.label, { x: x + 6, y: y + 3, size: 6.6, font: bold, color: brand.white });
    x += column.width;
  });
  y -= 20;
  report.statements.forEach((item, index) => {
    const values = [
      item.periodEnd,
      item.duration,
      displayFinancialValue(item.metricsByLabel['Sales revenue'], 'Sales revenue', { compact: true }),
      displayFinancialValue(item.metricsByLabel['Net income'], 'Net income', { compact: true }),
      displayFinancialValue(item.metricsByLabel['Total assets'], 'Total assets', { compact: true }),
      displayFinancialValue(item.metricsByLabel['Net worth'], 'Net worth', { compact: true }),
    ];
    if (index % 2) cover.drawRectangle({ x: margin, y: y - 3, width: contentWidth, height: 20, color: brand.surface });
    x = margin;
    values.forEach((value, valueIndex) => {
      cover.drawText(safePdfText(value || '-'), { x: x + 6, y: y + 3, size: 7.2, font: valueIndex === 0 ? bold : regular, color: brand.ink });
      x += columns[valueIndex].width;
    });
    y -= 20;
  });

  report.statements.forEach((item) => {
    const page = pdf.addPage(pageSize);
    let statementY = drawBrand(page, true);
    page.drawText(`Statement ending ${safePdfText(item.periodEnd)}`, { x: margin, y: statementY, size: 18, font: bold, color: brand.navy });
    statementY -= 23;
    page.drawText(`Period: ${safePdfText(item.periodStart)} to ${safePdfText(item.periodEnd)}  |  Duration: ${safePdfText(item.duration)}  |  Currency: ${safePdfText(item.currency)}`, { x: margin, y: statementY, size: 8.5, font: regular, color: brand.muted });
    statementY -= 25;

    FINANCIAL_GROUPS.forEach((group) => {
      const rows = group.labels.filter((label) => item.metricsByLabel[label]);
      if (!rows.length) return;
      page.drawRectangle({ x: margin, y: statementY - 3, width: contentWidth, height: 21, color: brand.navy });
      page.drawText(group.title.toUpperCase(), { x: margin + 8, y: statementY + 3, size: 7.4, font: bold, color: brand.white });
      statementY -= 19;
      rows.forEach((label, index) => {
        if (index % 2) page.drawRectangle({ x: margin, y: statementY - 3, width: contentWidth, height: 18, color: brand.surface });
        page.drawText(safePdfText(label), { x: margin + 8, y: statementY + 2, size: 8, font: regular, color: brand.ink });
        const value = safePdfText(displayFinancialValue(item.metricsByLabel[label], label));
        const valueWidth = bold.widthOfTextAtSize(value, 8);
        page.drawText(value, { x: pageSize[0] - margin - 8 - valueWidth, y: statementY + 2, size: 8, font: bold, color: brand.ink });
        statementY -= 18;
      });
      statementY -= 12;
    });
  });

  const pages = pdf.getPages();
  pages.forEach((page, index) => {
    page.drawLine({ start: { x: margin, y: 31 }, end: { x: pageSize[0] - margin, y: 31 }, thickness: 0.6, color: brand.border });
    page.drawText('Direct D&B data - not AI-generated', { x: margin, y: 17, size: 7, font: regular, color: brand.muted });
    const footer = `CrediArc  |  Page ${index + 1} of ${pages.length}`;
    page.drawText(footer, { x: pageSize[0] - margin - regular.widthOfTextAtSize(footer, 7), y: 17, size: 7, font: regular, color: brand.muted });
  });

  return pdf.save();
}

export async function createGenericFinancialReportPdf({ markdownText, companyName = 'Company', logoBytes = null }) {
  const pdf = await PDFDocument.create();
  pdf.setTitle(`${companyName} Financial Report`);
  pdf.setAuthor('CrediArc');
  pdf.setCreator('CrediArc Risk Platform');
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  let logo = null;
  if (logoBytes) {
    try { logo = await pdf.embedPng(logoBytes); } catch (error) { logo = null; }
  }
  const pageSize = [595.28, 841.89];
  const margin = 42;
  const width = pageSize[0] - margin * 2;
  let page;
  let y;

  const addPage = () => {
    page = pdf.addPage(pageSize);
    if (logo) page.drawImage(logo, { x: margin, y: 785, width: 112, height: 42 });
    else page.drawText('CrediArc', { x: margin, y: 802, size: 19, font: bold, color: brand.navy });
    page.drawLine({ start: { x: margin, y: 776 }, end: { x: pageSize[0] - margin, y: 776 }, thickness: 1, color: brand.border });
    y = 748;
  };

  const wrap = (text, font, size, maxWidth) => {
    const words = safePdfText(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) line = candidate;
      else {
        if (line) lines.push(line);
        line = word;
      }
    });
    if (line) lines.push(line);
    return lines.length ? lines : [''];
  };

  addPage();
  page.drawText('Financial Report', { x: margin, y, size: 23, font: bold, color: brand.navy });
  y -= 25;
  page.drawText(safePdfText(companyName), { x: margin, y, size: 12, font: bold, color: brand.ink });
  y -= 28;

  const rawLines = String(markdownText || '').split(/\r?\n/);
  rawLines.forEach((raw) => {
    const trimmed = raw.trim();
    if (!trimmed || /^\|\s*-+/.test(trimmed)) {
      y -= trimmed ? 0 : 5;
      return;
    }
    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    const isTable = trimmed.startsWith('|') && trimmed.endsWith('|');
    const text = cleanMarkdownValue(
      isTable
        ? trimmed.slice(1, -1).split(/(?<!\\)\|/).map((cell) => cell.trim()).join('   |   ')
        : (heading?.[2] || trimmed.replace(/^>\s*/, '')),
    );
    const font = heading ? bold : regular;
    const size = heading ? (heading[1].length === 1 ? 17 : heading[1].length === 2 ? 13 : 10.5) : (isTable ? 7.5 : 9);
    const lineHeight = size + 4;
    const wrapped = wrap(text, font, size, width);
    const needed = wrapped.length * lineHeight + (heading ? 7 : 2);
    if (y - needed < 45) addPage();
    wrapped.forEach((line) => {
      page.drawText(line, { x: margin, y, size, font, color: heading ? brand.navy : brand.ink });
      y -= lineHeight;
    });
    y -= heading ? 7 : 2;
  });

  const pages = pdf.getPages();
  pages.forEach((pdfPage, index) => {
    pdfPage.drawLine({ start: { x: margin, y: 31 }, end: { x: pageSize[0] - margin, y: 31 }, thickness: 0.6, color: brand.border });
    pdfPage.drawText('CrediArc Financial Intelligence', { x: margin, y: 17, size: 7, font: regular, color: brand.muted });
    const footer = `Page ${index + 1} of ${pages.length}`;
    pdfPage.drawText(footer, { x: pageSize[0] - margin - regular.widthOfTextAtSize(footer, 7), y: 17, size: 7, font: regular, color: brand.muted });
  });
  return pdf.save();
}
